import React, { useState, useEffect, useContext } from 'react';

import { Form, Formik } from 'formik';

import Button from '@components/common/Button/Button';
import Input from '@components/common/Formik/FormikInputField';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Radio from '@components/common/Radio/Radio';
import RadioGroup from '@components/common/RadioGroup/RadioGroup';
import Text from '@components/common/Text/Text';
import PotContext from '@components/lpc/Context/Pot/PotContext';
import PotService from '@services/domain/Lpc/PotService';
import {
    potSettingsParticipationsRegular,
    potSettingsParticipationsSuggested,
    potSettingsParticipationsFixed,
} from '@validations/potSchema';

import css from './ParticipationsForm.module.scss';

type GeneralFormProps = {
    potRef: string;
    onCloseModal: () => void;
    poolContribution: { contributionType: string; contributionAmount?: number };
};

const ParticipationsForm = ({ onCloseModal, potRef, poolContribution }: GeneralFormProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'lpc-create-pot']);
    const potContext = useContext(PotContext);
    const { getUpdatedPot } = potContext;

    const [isLoading, setIsLoading] = useState(false);
    const [contributionType, setContributionType] = useState('');

    const getInitialValues = () => {
        return {
            contributionType: poolContribution.contributionType,
            contributionAmountSuggested:
                poolContribution.contributionType === 'SUGGESTED' ? poolContribution.contributionAmount : '',
            contributionAmountFixed:
                poolContribution.contributionType === 'FIXED' ? poolContribution.contributionAmount : '',
        };
    };

    useEffect(() => {
        setContributionType(poolContribution.contributionType);
    }, [poolContribution.contributionType]);

    const onSubmit = (values) => {
        setIsLoading(true);

        let body;
        if (contributionType === 'REGULAR') {
            body = {
                contributionType: contributionType,
            };
        }
        if (contributionType === 'SUGGESTED') {
            body = {
                contributionType: contributionType,
                contributionAmount: values.contributionAmountSuggested,
            };
        }
        if (contributionType === 'FIXED') {
            body = {
                contributionType: contributionType,
                contributionAmount: values.contributionAmountFixed,
            };
        }
        PotService.updatePotSettingsParticipations(potRef, body)
            .then((res) => res.status === 200 && getUpdatedPot(potRef))
            .then(() => onCloseModal())
            .catch((err) => err)
            .finally(() => setIsLoading(false));
    };

    const onCancel = () => {
        onCloseModal();
    };

    const getValidationSchema = () => {
        if (contributionType === 'REGULAR') {
            return potSettingsParticipationsRegular;
        }
        if (contributionType === 'SUGGESTED') {
            return potSettingsParticipationsSuggested;
        }
        if (contributionType === 'FIXED') {
            return potSettingsParticipationsFixed;
        }
    };

    return (
        <div>
            <Text variant={'mini_label'} color={'txt-primary'}>
                {t('settings.participations.type-title')}
            </Text>
            <Text variant={'caption_01'} color={'txt-primary'}>
                {t('settings.participations.type-desc')}
            </Text>
            <div className={css.form__divider} />

            <Formik
                validationSchema={getValidationSchema()}
                initialValues={getInitialValues()}
                onSubmit={(values) => onSubmit(values)}
            >
                {({ setFieldValue }) => (
                    <Form>
                        <>
                            <RadioGroup customClass={css.radio__group} name="contributionType" hideErrors>
                                <div className={css.form__input}>
                                    <Radio
                                        inputValue={'REGULAR'}
                                        inputName="contributionType"
                                        label={t('form.free-label')}
                                        desc={t('form.free-desc')}
                                        onChange={(value) => {
                                            setContributionType(value);
                                        }}
                                        customClass={css.form__input__label}
                                        containerClass={css.form__input__desc}
                                    />
                                </div>
                                <div
                                    onClick={() => setFieldValue('contributionAmountFixed', '')}
                                    className={css.form__input}
                                >
                                    <Radio
                                        inputValue={'SUGGESTED'}
                                        inputName="contributionType"
                                        label={t('form.suggested-label')}
                                        desc={t('form.suggested-desc')}
                                        onChange={(value) => {
                                            setContributionType(value);
                                        }}
                                        customClass={css.form__input__label}
                                        containerClass={css.form__input__desc}
                                    />
                                    <div className={css.form__input__collapse}>
                                        <Input
                                            isAmount
                                            name="contributionAmountSuggested"
                                            type="text"
                                            placeholder={t('form.suggested-placeholder')}
                                            disabled={contributionType !== 'SUGGESTED'}
                                        />
                                    </div>
                                </div>
                                <div
                                    onClick={() => setFieldValue('contributionAmountSuggested', '')}
                                    className={css.form__input}
                                >
                                    <Radio
                                        inputValue={'FIXED'}
                                        inputName="contributionType"
                                        label={t('form.fixed-label')}
                                        desc={t('form.fixed-desc')}
                                        onChange={(value) => {
                                            setContributionType(value);
                                        }}
                                        customClass={css.form__input__label}
                                        containerClass={css.form__input__desc}
                                    />
                                    <div className={css.form__input__collapse}>
                                        <Input
                                            isAmount
                                            name="contributionAmountFixed"
                                            type="text"
                                            placeholder={t('form.suggested-placeholder')}
                                            disabled={contributionType !== 'FIXED'}
                                        />
                                    </div>
                                </div>
                            </RadioGroup>

                            {/* SUBMIT */}
                            <div className={css.form__submit}>
                                <Button
                                    type={'button'}
                                    customClass={css.form__submit__cta}
                                    variant={'secondary'}
                                    onClick={() => onCancel()}
                                >
                                    {t('settings.cancel')}
                                </Button>
                                <Button
                                    type={'submit'}
                                    margin={'0 0 0 8px'}
                                    variant={'primary'}
                                    customClass={css.form__submit__cta}
                                    isLoading={isLoading}
                                    isDisabled={isLoading}
                                >
                                    {t('settings.confirm')}
                                </Button>
                            </div>
                        </>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default ParticipationsForm;
